import React, { useState } from 'react';
import { Star, Send, MessageSquare, Shield, Clock, Users, Car, ThumbsUp } from 'lucide-react';
import toast from 'react-hot-toast';
import { useAuth } from '../context/AuthContext';
import { fetchWithAuth } from '../services/fetchWithAuth';

const FeedbackForm = ({ matchId, otherUser, rideDetails, onSubmit, onCancel }) => {
  const { logout } = useAuth();
  const [feedback, setFeedback] = useState('');
  const [manualRating, setManualRating] = useState(null);
  const [categories, setCategories] = useState([]);
  const [isAnonymous, setIsAnonymous] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [aiRating, setAiRating] = useState(null);
  const [showAiRating, setShowAiRating] = useState(false);

  const categoryOptions = [
    { id: 'punctuality', label: 'Punctuality', icon: Clock },
    { id: 'safety', label: 'Safety', icon: Shield },
    { id: 'communication', label: 'Communication', icon: MessageSquare },
    { id: 'comfort', label: 'Comfort', icon: Car },
    { id: 'cleanliness', label: 'Cleanliness', icon: ThumbsUp },
    { id: 'professionalism', label: 'Professionalism', icon: Users },
    { id: 'friendliness', label: 'Friendliness', icon: ThumbsUp },
    { id: 'value_for_money', label: 'Value for Money', icon: Star },
    { id: 'overall_experience', label: 'Overall Experience', icon: Star }
  ];

  const handleCategoryToggle = (categoryId) => {
    setCategories(prev => 
      prev.includes(categoryId) 
        ? prev.filter(id => id !== categoryId)
        : [...prev, categoryId]
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!feedback.trim()) {
      toast.error('Please provide feedback');
      return;
    }

    if (feedback.length < 10) {
      toast.error('Feedback must be at least 10 characters long');
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await fetchWithAuth('/api/feedback/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          matchId: matchId || 'dummy-id', // fallback for testing
          userFeedback: feedback.trim(),
          manualRating: manualRating || 5,
          categories: Array.isArray(categories) ? categories : [],
          isAnonymous: !!isAnonymous
        })
      }, logout);

      const data = await response.json();

      if (response.ok) {
        toast.success('Feedback submitted successfully!');
        if (onSubmit) {
          onSubmit({
            userFeedback: feedback.trim(),
            manualRating: manualRating || 5,
            aiRating: aiRating,
            categories: Array.isArray(categories) ? categories : [],
            isAnonymous: !!isAnonymous
          });
        }
      } else {
        throw new Error(data.error || 'Failed to submit feedback');
      }
    } catch (error) {
      console.error('Submit feedback error:', error);
      toast.error(error.message || 'Failed to submit feedback');
    } finally {
      setIsSubmitting(false);
    }
  };

  const generateAiRating = async () => {
    if (!feedback.trim() || feedback.length < 10) {
      toast.error('Please provide at least 10 characters of feedback to generate AI rating');
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await fetchWithAuth('/api/feedback/generate-rating', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ feedback: feedback.trim() })
      }, logout);

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to generate AI rating');
      }

      console.log('AI Rating Response:', data);
      
      // Validate that rating is a valid number
      if (typeof data.rating !== 'number' || isNaN(data.rating)) {
        throw new Error('Invalid rating received from server');
      }
      
      setAiRating({
        rating: data.rating,
        confidence: data.confidence || 0.5,
        method: 'rule_based',
        adjustments: data.adjustments || {}
      });
      setShowAiRating(true);
      toast.success('AI rating generated!');
    } catch (error) {
      console.error('Generate AI rating error:', error);
      toast.error(error.message || 'Failed to generate AI rating');
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderStars = (rating, interactive = false, onRatingChange = null) => {
    return (
      <div className="flex items-center space-x-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <button
            key={star}
            type={interactive ? 'button' : 'button'}
            onClick={interactive ? () => onRatingChange(star) : undefined}
            className={`${
              interactive ? 'cursor-pointer hover:scale-110 transition-transform' : 'cursor-default'
            }`}
            disabled={!interactive}
          >
            <Star
              className={`w-6 h-6 ${
                star <= rating
                  ? 'text-yellow-400 fill-current'
                  : 'text-gray-300'
              }`}
            />
          </button>
        ))}
        {interactive && (
          <span className="ml-2 text-sm text-gray-600">
            {rating ? `${rating}/5` : 'Select rating'}
          </span>
        )}
      </div>
    );
  };

  return (
    <div className="bg-white rounded-lg shadow-lg p-6 max-w-2xl mx-auto">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Rate Your Ride Companion</h2>
        <p className="text-gray-600">Share your experience and help others make informed decisions</p>
      </div>

      {/* Ride Details */}
      <div className="bg-gray-50 rounded-lg p-4 mb-6">
        <div className="flex items-center space-x-4">
          <img
            src={otherUser.profilePicture || `https://ui-avatars.com/api/?name=${otherUser.name}&background=random`}
            alt={otherUser.name}
            className="w-12 h-12 rounded-full object-cover"
          />
          <div>
            <h3 className="font-semibold text-gray-900">{otherUser.name}</h3>
            <div className="flex items-center space-x-2 text-sm text-gray-600">
              <Star className="w-4 h-4 text-yellow-400 fill-current" />
              <span>{otherUser.rating?.average || 0}</span>
              <span>({otherUser.rating?.count || 0} rides)</span>
            </div>
          </div>
        </div>
        
        <div className="mt-3 text-sm text-gray-600">
          <div className="flex items-center space-x-2">
            <Car className="w-4 h-4" />
            <span>{rideDetails.source} → {rideDetails.destination}</span>
          </div>
          <div className="flex items-center space-x-2 mt-1">
            <Clock className="w-4 h-4" />
            <span>{rideDetails.rideTime} • {new Date(rideDetails.rideDate).toLocaleDateString()}</span>
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Feedback Text */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Your Feedback *
          </label>
          <textarea
            value={feedback}
            onChange={(e) => setFeedback(e.target.value)}
            placeholder="Share your experience with this ride companion. Be specific about what went well or what could be improved..."
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            rows={4}
            maxLength={1000}
            required
          />
          <div className="flex justify-between items-center mt-1">
            <span className="text-xs text-gray-500">
              {feedback.length}/1000 characters
            </span>
            <button
              type="button"
              onClick={generateAiRating}
              disabled={isSubmitting || feedback.length < 10}
              className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded hover:bg-blue-200 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Generate AI Rating
            </button>
          </div>
        </div>

        {/* AI Rating Display */}
        {showAiRating && aiRating && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <h4 className="font-medium text-blue-900">AI-Generated Rating</h4>
              <span className="text-xs bg-blue-200 text-blue-800 px-2 py-1 rounded">
                {aiRating.method === 'ai_model' ? 'AI Model' : 'Rule-based'}
              </span>
            </div>
            <div className="flex items-center space-x-4">
              {renderStars(aiRating.rating)}
              <div className="text-sm text-blue-700">
                <div>Confidence: {aiRating.confidence ? (aiRating.confidence * 100).toFixed(1) : 'N/A'}%</div>
                {aiRating.adjustments && (
                  <div className="text-xs mt-1">
                    {aiRating.adjustments.safety && <span className="inline-block bg-red-100 text-red-700 px-1 rounded mr-1">Safety</span>}
                    {aiRating.adjustments.punctuality && <span className="inline-block bg-orange-100 text-orange-700 px-1 rounded mr-1">Punctuality</span>}
                    {aiRating.adjustments.communication && <span className="inline-block bg-yellow-100 text-yellow-700 px-1 rounded mr-1">Communication</span>}
                    {aiRating.adjustments.positive && <span className="inline-block bg-green-100 text-green-700 px-1 rounded">Positive</span>}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Manual Rating */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Your Rating (Optional - Override AI Rating)
          </label>
          {renderStars(manualRating || 0, true, setManualRating)}
          <p className="text-xs text-gray-500 mt-1">
            Leave empty to use AI-generated rating, or select your own rating
          </p>
        </div>

        {/* Categories */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Categories (Select all that apply)
          </label>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
            {categoryOptions.map((category) => {
              const Icon = category.icon;
              return (
                <button
                  key={category.id}
                  type="button"
                  onClick={() => handleCategoryToggle(category.id)}
                  className={`flex items-center space-x-2 px-3 py-2 rounded-lg border text-sm transition-colors ${
                    categories.includes(category.id)
                      ? 'bg-blue-100 border-blue-300 text-blue-700'
                      : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  <span>{category.label}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Anonymous Option */}
        <div className="flex items-center space-x-2">
          <input
            type="checkbox"
            id="anonymous"
            checked={isAnonymous}
            onChange={(e) => setIsAnonymous(e.target.checked)}
            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
          />
          <label htmlFor="anonymous" className="text-sm text-gray-700">
            Submit feedback anonymously
          </label>
        </div>

        {/* Submit Buttons */}
        <div className="flex space-x-3 pt-4">
          <button
            type="submit"
            disabled={isSubmitting || !feedback.trim()}
            className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
          >
            {isSubmitting ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                <span>Submitting...</span>
              </>
            ) : (
              <>
                <Send className="w-4 h-4" />
                <span>Submit Feedback</span>
              </>
            )}
          </button>
          
          {onCancel && (
            <button
              type="button"
              onClick={onCancel}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
          )}
        </div>
      </form>
    </div>
  );
};

export default FeedbackForm; 